import type { SDQDimensionScore, SDQComposite } from '../../../types';

export const DIMENSION_LABELS: Record<string, string> = {
  completeness: 'Completeness',
  accuracy: 'Accuracy',
  consistency: 'Consistency',
  conformity: 'Conformity',
  duplication: 'Duplication',
  currency: 'Currency',
  semantic_coherence: 'Semantic Coherence',
  referential_integrity: 'Ref. Integrity',
  processability: 'Processability',
  provenance: 'Provenance',
};

export const DIMENSION_DESCRIPTIONS: Record<string, string> = {
  completeness:
    'Measures null rates, column coverage, and row presence. Null thresholds: 0% = 100, ≤5% = 90, ≤15% = 70, ≤30% = 40. Weighted 40% null rate, 30% column coverage, 30% row completeness.',
  accuracy:
    'Validates data correctness via regex/format checks (emails, phones, dates), value range plausibility, and allowed-value rules. Sampled on 500 rows. Weighted 40% syntactic validation, 30% plausibility, 30% column rules.',
  consistency:
    'Checks logical coherence across columns and tables — e.g., created_date < modified_date, start < end. Weighted 40% cross-column checks, 35% cross-table, 25% statistical consistency.',
  conformity:
    'Verifies data conforms to the expected schema — column names match, data types align (numeric, string, date), and constraints (NOT NULL, etc.) are satisfied. Weighted 40% schema match, 30% type conformity, 30% constraint compliance.',
  duplication:
    'Detects exact duplicate rows and key-uniqueness violations. Thresholds: <1% duplicates = 90, <5% = 60. Weighted 50% exact duplicates, 30% fuzzy duplicates, 20% key uniqueness.',
  currency:
    'Evaluates data freshness by checking timestamp columns (modified_date, updated_at, load_timestamp) against a staleness threshold (default: 1 year). Weighted 40% row staleness, 30% partition freshness, 30% metadata currency.',
  semantic_coherence:
    'Analyzes statistical distribution plausibility — detects extreme outliers using the IQR method (3.0x threshold) and flags skewed distributions. Weighted 40% distribution plausibility, 30% outlier detection, 30% pattern consistency.',
  referential_integrity:
    'Checks that foreign key values resolve to valid records in reference tables. Samples 1,000 source and 10,000 reference rows. Weighted 50% FK validity, 30% orphan records, 20% cross-table resolution.',
  processability:
    'Validates the dataset can be parsed and processed — schema is readable, encoding is valid, and file format is compliant. Returns 0 if no columns detected. Weighted 40% schema parsability, 30% encoding validity, 30% format compliance.',
  provenance:
    'Checks presence of data lineage metadata — source_system, etl_job, load_timestamp, and similar audit trail fields. Weighted 40% pipeline lineage, 30% source tracking, 30% ETL metadata.',
};

export const DIMENSION_SHORT_LABELS: Record<string, string> = {
  completeness: 'Complete',
  accuracy: 'Accuracy',
  consistency: 'Consist.',
  conformity: 'Conform.',
  duplication: 'Duplicat.',
  currency: 'Currency',
  semantic_coherence: 'Semantic',
  referential_integrity: 'Ref. Int.',
  processability: 'Process.',
  provenance: 'Proven.',
};

export function scoreColor(score: number): string {
  if (score >= 90) return '#10B981'; // green
  if (score >= 75) return '#3B82F6'; // blue
  if (score >= 60) return '#F59E0B'; // amber
  return '#EF4444'; // red
}

export function scoreBg(score: number): string {
  if (score >= 90) return 'bg-green-500/15 text-green-400';
  if (score >= 75) return 'bg-blue-500/15 text-blue-400';
  if (score >= 60) return 'bg-amber-500/15 text-amber-400';
  return 'bg-red-500/15 text-red-400';
}

export function gradeColor(grade: string): string {
  const map: Record<string, string> = {
    A: '#10B981', B: '#3B82F6', C: '#F59E0B', D: '#EF4444', F: '#EF4444',
  };
  return map[grade] || '#64748B';
}

export function severityColor(severity: string): string {
  const map: Record<string, string> = {
    critical: '#EF4444', high: '#F97316', medium: '#F59E0B', low: '#3B82F6', info: '#6B7280',
  };
  return map[severity] || '#6B7280';
}

export function severityBg(severity: string): string {
  const map: Record<string, string> = {
    critical: 'bg-red-500/20 text-red-400',
    high: 'bg-orange-500/20 text-orange-400',
    medium: 'bg-amber-500/20 text-amber-400',
    low: 'bg-blue-500/20 text-blue-400',
    info: 'bg-cargo-panel text-cargo-muted',
  };
  return map[severity] || 'bg-cargo-panel text-cargo-muted';
}

export function getGrade(score: number): string {
  if (score >= 90) return 'A';
  if (score >= 75) return 'B';
  if (score >= 60) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

export function sortDimensionsByScore(
  dimensions: Record<string, SDQDimensionScore>
): [string, SDQDimensionScore][] {
  return Object.entries(dimensions)
    .filter(([, d]) => d.enabled)
    .sort(([, a], [, b]) => a.score - b.score);
}
